import { useReducer, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { getTableTransactionsRest } from '@/services/transactions/transactions-service';
import TransactionsTable from '@/pages/common/TransactionsTable';
import DebouncedInput from '@/components/DebounceInput';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { TransactionDto } from '@repo/common-types';
import CopyField from '@/common/forms/CopyField';
import { config } from '@repo/common-types';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';

import {
  tableTransactionsQueryReducer,
  initialState,
  TableTransactionsActionType,
} from '../table-transactions-query-reducer';
import { getStatusBadgeColor } from '@/pages/deposits/utils/get-status-badge-color';

const columns: ColumnDef<TransactionDto>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: (info) => format(info.getValue() as Date, 'dd MMM yyyy HH:mm'),
    sortDescFirst: false,
    enableSorting: true,
  },
  {
    accessorKey: 'txId',
    header: 'Transaction ID',
    sortUndefined: 'last',
    cell: (info) => {
      const cellData = info.getValue();
      return (
        <CopyField
          className="w-100"
          name="txId"
          label={false}
          value={cellData}
          link={`${config.deposits.polygon.blockExplorerUrl}/tx/${cellData}`}
        />
      );
    },
    size: 200,
  },
  {
    accessorKey: 'tableName',
    header: 'Table Name',
    cell: (info) => info.getValue() || '',
  },
  {
    accessorKey: 'userAlias',
    header: 'User',
    cell: (info) => info.getValue() || '',
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => (
      <span className={`badge ${getStatusBadgeColor(row.original.status)}`}>{row.original.status}</span>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: true,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: (info) => `${Number(info.getValue() || 0).toFixed(2)}`,
    enableSorting: true,
  },
  {
    accessorKey: 'addressFrom',
    header: 'From',
    cell: (info) => {
      const cellData = info.getValue();
      return (
        <CopyField
          className="w-100"
          name="txId"
          label={false}
          value={cellData}
          link={`${config.deposits.polygon.blockExplorerUrl}/address/${cellData}`}
        />
      );
    },
    size: 200,
  },
  {
    accessorKey: 'addressTo',
    header: 'To',
    cell: (info) => {
      const cellData = info.getValue();
      return (
        <CopyField
          className="w-100"
          name="txId"
          label={false}
          value={cellData}
          link={`${config.deposits.polygon.blockExplorerUrl}/address/${cellData}`}
        />
      );
    },
    size: 200,
  },
];

const TransactionsSection = ({ tableId }: { tableId: string }) => {
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  const [queryParams, dispatch] = useReducer(tableTransactionsQueryReducer, initialState);

  const { sorting, filter, take, skip } = queryParams;

  const {
    data: transactionsResponse,
    isLoading: isTransactionsLoading,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ['tableTransactions', tableId, queryParams],
    queryFn: () => getTableTransactionsRest(tableId, { sorts: convertSortingToApiFormat(sorting), filter, take, skip }),
  });
  const handlePageChange = useCallback((skip: number, take: number) => {
    dispatch({ type: TableTransactionsActionType.SET_PAGINATION, payload: { skip, take } });
  }, []);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(queryParams.sorting) : updater;
      dispatch({ type: TableTransactionsActionType.SET_SORTING, payload: newSorting });
    },
    [queryParams.sorting],
  );

  const handleSearch = useCallback((value: string | number) => {
    setIsDebouncing(true);
    dispatch({ type: TableTransactionsActionType.SET_SEARCH, payload: value.toString() });
    setIsDebouncing(false);
  }, []);
  const renderTransactionsTable = useMemo(() => {
    return (
      <TransactionsTable
        transactions={transactionsResponse?.transactions || []}
        refreshTable={refetchTransactions}
        totalCount={transactionsResponse?.totalCount || 0}
        onPageChange={handlePageChange}
        take={queryParams.take}
        skip={queryParams.skip}
        isLoadingData={isTransactionsLoading}
        onSortingChange={handleSortingChange}
        columns={columns}
        sorting={queryParams.sorting}
      />
    );
  }, [transactionsResponse?.transactions, queryParams.take, queryParams.skip]);

  return (
    <div className="my-4">
      <div className="d-flex flex-row gap-3">
        <h3>Transactions</h3>
        <DebouncedInput
          className="form-control w-auto"
          placeholder="Search transactions..."
          onChange={handleSearch}
          value={queryParams.filter?.search || ''}
          isLoading={isDebouncing}
        />
        {Object.values(queryParams.filter || {}).some(Boolean) && (
          <Button
            size={ButtonSizes.sm}
            variant={ButtonVariants.primary}
            onClick={() => dispatch({ type: TableTransactionsActionType.RESET_FILTERS })}
            className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
          >
            Clear Filters
          </Button>
        )}
      </div>
      {renderTransactionsTable}
    </div>
  );
};

export default TransactionsSection;
